import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import { useEventReviewsQuery } from '../../../api/use-event-reviews-query';
import { useUpdateEventReviewStatusMutation } from '../../../api/use-update-event-review-status-mutation';
import { EVENT_FEEDBACK_STATUS } from '../mapEventReviews';
import { BRAND_ORANGE } from '../../../styles/portalTheme';
import FeedbackStatusConfirmDialog, {
  getFeedbackConfirmActionKey,
} from './FeedbackStatusConfirmDialog';

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '1.125rem',
  color: 'text.primary',
  mb: 1.75,
};

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: 1.5,
  p: 2.25,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const actionsBoxSx = {
  display: 'flex',
  gap: 1,
  mt: 'auto',
  pt: 1.5,
  borderTop: '1px solid',
  borderTopColor: 'divider',
};

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  flex: 1,
  justifyContent: 'center',
  gap: 0.75,
};

const reviewGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 1.5,
};

const RatingStars = ({ rating }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 1.25 }}>
    {[1, 2, 3, 4, 5].map((value) => (
      <StarIcon
        key={value}
        sx={{
          fontSize: 18,
          color: value <= rating ? '#f43f5e' : 'divider',
        }}
      />
    ))}
  </Box>
);

const metaLabelSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: 'text.disabled',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: 0.25,
};

const metaValueSx = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'text.secondary',
  lineHeight: 1.4,
};

const FeedbackCard = ({ item, actions }) => {
  const initial = (item.fullName || '?').charAt(0).toUpperCase();

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Avatar
          src={item.avatarUrl || undefined}
          alt={item.fullName}
          sx={{ width: 44, height: 44 }}
        >
          {initial}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
          >
            {item.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {item.role}
          </Typography>
          <RatingStars rating={item.rating} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1.25,
              mb: item.comment ? 1.25 : 0,
            }}
          >
            <Box>
              <Typography sx={metaLabelSx}>Table match</Typography>
              <Typography sx={metaValueSx}>{item.tableMatchScore || '—'}</Typography>
            </Box>
            <Box>
              <Typography sx={metaLabelSx}>Highlight</Typography>
              <Typography sx={metaValueSx}>{item.highlight || '—'}</Typography>
            </Box>
          </Box>
          {item.comment && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.65,
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
              }}
            >
              &ldquo;{item.comment}&rdquo;
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={actionsBoxSx}>{actions}</Box>
    </Box>
  );
};

const FeedbackSection = ({ title, items, emptyMessage, renderActions }) => (
  <Box sx={{ mb: 4 }}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {items.length ? (
      <Box sx={reviewGridSx}>
        {items.map((item) => (
          <FeedbackCard key={item.id} item={item} actions={renderActions(item)} />
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    )}
  </Box>
);

const EventFeedbackModerationPanel = ({ eventId }) => {
  const [pendingAction, setPendingAction] = useState(null);
  const { data, isLoading, isError, error, isFetching } = useEventReviewsQuery(eventId);
  const updateReviewStatusMutation = useUpdateEventReviewStatusMutation({
    onSuccess: (result) => {
      toast.success(result.message || 'Review status updated');
      setPendingAction(null);
    },
    onError: (updateError) => {
      toast.error(updateError?.message || 'Failed to update review status');
    },
  });

  const pendingItems = data?.pending ?? [];
  const approvedItems = data?.approved ?? [];
  const updatingReviewId = updateReviewStatusMutation.isPending
    ? updateReviewStatusMutation.variables?.reviewId
    : null;
  const isUpdating = updateReviewStatusMutation.isPending;

  const requestStatusUpdate = (item, status) => {
    if (!item?.id || isUpdating) return;

    const actionKey = getFeedbackConfirmActionKey(status, {
      isHiddenItem: item.status === EVENT_FEEDBACK_STATUS.HIDDEN,
    });
    if (!actionKey) return;

    setPendingAction({ item, status, actionKey });
  };

  const handleConfirmAction = () => {
    if (!pendingAction?.item?.id || isUpdating) return;
    updateReviewStatusMutation.mutate({
      reviewId: pendingAction.item.id,
      status: pendingAction.status,
      eventId,
    });
  };

  const handleCloseConfirm = () => {
    if (isUpdating) return;
    setPendingAction(null);
  };

  const isConfirmingPendingAction =
    isUpdating &&
    pendingAction?.item?.id === updatingReviewId &&
    updateReviewStatusMutation.variables?.status === pendingAction?.status;

  if (isLoading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" color="error">
          {error?.message || 'Failed to load event reviews.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2.5, position: 'relative' }}>
      {isFetching && !isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={20} sx={{ color: BRAND_ORANGE }} />
        </Box>
      ) : null}

      <FeedbackSection
        title="Pending Review"
        items={pendingItems}
        emptyMessage="No feedback pending review."
        renderActions={(item) => {
          const isRowUpdating = updatingReviewId === item.id;

          return (
            <>
              <LoadingButton
                variant="contained"
                startIcon={<CheckIcon />}
                loading={
                  isRowUpdating &&
                  updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.APPROVED
                }
                disabled={isUpdating}
                onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.APPROVED)}
                sx={{
                  ...actionButtonSx,
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                }}
              >
                Approve
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                startIcon={<CloseIcon />}
                loading={
                  isRowUpdating &&
                  updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.REJECTED
                }
                disabled={isUpdating}
                onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.REJECTED)}
                sx={{
                  ...actionButtonSx,
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': { borderColor: 'error.dark', bgcolor: 'error.light' },
                }}
              >
                Reject
              </LoadingButton>
            </>
          );
        }}
      />

      <FeedbackSection
        title="Approved Feedback"
        items={approvedItems}
        emptyMessage="No approved feedback yet."
        renderActions={(item) => {
          const isRowUpdating = updatingReviewId === item.id;
          const isHidden = item.status === EVENT_FEEDBACK_STATUS.HIDDEN;

          if (isHidden) {
            return (
              <>
                <LoadingButton
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  loading={
                    isRowUpdating &&
                    updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.APPROVED
                  }
                  disabled={isUpdating}
                  onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.APPROVED)}
                  sx={{
                    ...actionButtonSx,
                    color: 'info.main',
                    borderColor: 'info.main',
                    '&:hover': { borderColor: 'info.dark', bgcolor: 'info.light' },
                  }}
                >
                  Unhide
                </LoadingButton>
                <LoadingButton
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  loading={
                    isRowUpdating &&
                    updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.DELETED
                  }
                  disabled={isUpdating}
                  onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.DELETED)}
                  sx={{
                    ...actionButtonSx,
                    color: 'error.main',
                    borderColor: 'error.main',
                    '&:hover': { borderColor: 'error.dark', bgcolor: 'error.light' },
                  }}
                >
                  Delete
                </LoadingButton>
              </>
            );
          }

          return (
            <>
              <LoadingButton
                variant="outlined"
                startIcon={<VisibilityOffOutlinedIcon />}
                loading={
                  isRowUpdating &&
                  updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.HIDDEN
                }
                disabled={isUpdating}
                onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.HIDDEN)}
                sx={{
                  ...actionButtonSx,
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.hover' },
                }}
              >
                Hide
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                loading={
                  isRowUpdating &&
                  updateReviewStatusMutation.variables?.status === EVENT_FEEDBACK_STATUS.DELETED
                }
                disabled={isUpdating}
                onClick={() => requestStatusUpdate(item, EVENT_FEEDBACK_STATUS.DELETED)}
                sx={{
                  ...actionButtonSx,
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': { borderColor: 'error.dark', bgcolor: 'error.light' },
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }}
      />

      <FeedbackStatusConfirmDialog
        open={Boolean(pendingAction)}
        actionKey={pendingAction?.actionKey}
        item={pendingAction?.item}
        confirming={isConfirmingPendingAction}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmAction}
      />
    </Box>
  );
};

export default EventFeedbackModerationPanel;
